import { useEffect, useMemo, useState } from "react";
import EmptyState from "../../../shared/components/EmptyState";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { ChallengeDetail } from "../../challenges/types/challengeAdmin";
import {
  isPendingPocoMatch,
  resolvedWinnerLoser,
} from "../../challenges/utils/pocoSchedule";
import { resolveSuper8RoundGroup } from "../../challenges/utils/super8Schedule";
import type { ParticipantCheckin } from "../services/eventRegistrationAdminService";
import { sideIsPresent, type MatchCheckInSide } from "./AdminMatchCheckInControl";

type WinnerSide = "dupla1" | "dupla2" | "";

type AdminChallengeRoundsEditorProps = {
  challengeDetail: ChallengeDetail | null;
  emptyMessage: string;
  finalLabel: string;
  isPoco: boolean;
  isSuper8: boolean;
  isTournamentChallenge: boolean;
  onClearWinner?: (roundIndex: number, matchIndex: number) => void;
  onToggleCheckInSide?: (side: MatchCheckInSide, checkedIn: boolean) => void;
  onToggleCourtStatus?: (
    roundIndex: number,
    matchIndex: number,
    match: any,
    status: "pending" | "scheduled" | "in_progress",
  ) => void;
  onUpdatePocoWinner: (
    roundIndex: number,
    matchIndex: number,
    winnerSide: WinnerSide,
  ) => void;
  onUpdateRoundScore: (
    roundIndex: number,
    matchIndex: number,
    field: "score1" | "score2",
    value: string,
  ) => void;
  pairLabelById: Record<string, string>;
  pendingCheckInSideId?: string | null;
  pendingCourtMatchId?: number | null;
  participantsCountForGroup: number;
  resolveRoundPairLabelWithPoints: (
    pairId: string,
    roundIndex: number,
  ) => string;
  rounds: any[];
  resultsOnly?: boolean;
  scoreMax: number;
  showClearAction?: boolean;
  participantCheckins?: ParticipantCheckin[];
  courtsCount?: number | null;
};

function normalizeCpf(value: string | null | undefined) {
  return String(value || "").replace(/\D/g, "").slice(0, 11);
}

export default function AdminChallengeRoundsEditor({
  challengeDetail,
  emptyMessage,
  finalLabel,
  isPoco,
  isSuper8,
  isTournamentChallenge,
  onClearWinner,
  onToggleCheckInSide,
  onToggleCourtStatus,
  onUpdatePocoWinner,
  onUpdateRoundScore,
  pairLabelById,
  pendingCheckInSideId = null,
  pendingCourtMatchId = null,
  participantsCountForGroup,
  resolveRoundPairLabelWithPoints,
  rounds,
  resultsOnly = false,
  scoreMax,
  showClearAction = false,
  participantCheckins = [],
  courtsCount = null,
}: AdminChallengeRoundsEditorProps) {
  const customTexts = siteTexts.admin.createChallengePage.customSettings;
  const timerTotalSeconds =
    !resultsOnly &&
    challengeDetail?.challengeModel === "custom" &&
    challengeDetail.gameType === "time" &&
    Number(challengeDetail.gameDurationMinutes || 0) > 0
      ? Math.floor(Number(challengeDetail.gameDurationMinutes)) * 60
      : 0;
  const [remainingSeconds, setRemainingSeconds] = useState(timerTotalSeconds);
  const [timerRunning, setTimerRunning] = useState(false);
  useEffect(() => {
    setRemainingSeconds(timerTotalSeconds);
    setTimerRunning(false);
  }, [timerTotalSeconds]);
  useEffect(() => {
    if (!timerRunning || remainingSeconds <= 0) return;
    const id = window.setInterval(() => {
      setRemainingSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [remainingSeconds, timerRunning]);
  useEffect(() => {
    if (remainingSeconds <= 0) setTimerRunning(false);
  }, [remainingSeconds]);
  const timerLabel = useMemo(() => {
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, [remainingSeconds]);
  const pairById = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<ChallengeDetail["pairs"]>[number]
    >();
    (challengeDetail?.pairs || []).forEach((pair) => {
      const id = String(pair.id || "").trim();
      if (id) map.set(id, pair);
    });
    return map;
  }, [challengeDetail?.pairs]);

  const globalCheckInSides = useMemo(() => {
    if (isPoco || isTournamentChallenge) return [];
    const byCpf = new Map<string, string>();
    (challengeDetail?.participants || []).forEach((participant) => {
      const cpf = normalizeCpf(participant.cpf);
      if (!cpf) return;
      byCpf.set(cpf, String(participant.name || "").trim() || cpf);
    });
    (challengeDetail?.pairs || []).forEach((pair) => {
      const cpf1 = normalizeCpf(pair.cpf1);
      const cpf2 = normalizeCpf(pair.cpf2);
      if (cpf1 && !byCpf.has(cpf1)) {
        byCpf.set(cpf1, String(pair.nome1 || "").trim() || cpf1);
      }
      if (cpf2 && !byCpf.has(cpf2)) {
        byCpf.set(cpf2, String(pair.nome2 || "").trim() || cpf2);
      }
    });
    return Array.from(byCpf.entries()).map(([cpf, label], index) => ({
      id: `global-player-${cpf || index}`,
      label,
      participantCpfs: [cpf],
      participants: [{ cpf, label }],
    }));
  }, [
    challengeDetail?.pairs,
    challengeDetail?.participants,
    isPoco,
    isTournamentChallenge,
  ]);

  const toggleGlobalCheckInSide = (
    side: MatchCheckInSide,
    checkedIn: boolean,
  ) => {
    onToggleCheckInSide?.(side, checkedIn);
  };

  const normalizeMatchStatus = (match: any) => {
    const hasScore =
      match?.score1 !== "" &&
      match?.score1 !== null &&
      match?.score1 !== undefined &&
      match?.score2 !== "" &&
      match?.score2 !== null &&
      match?.score2 !== undefined;
    const score1 = Number(match?.score1);
    const score2 = Number(match?.score2);
    if (hasScore && Number.isFinite(score1) && Number.isFinite(score2)) {
      return "finished";
    }
    const status = String(match?.status || "")
      .trim()
      .toLowerCase();
    if (status === "scheduled") return "scheduled";
    if (status === "in_progress") return "in_progress";
    if (status === "finished") return "finished";
    return "pending";
  };

  const matchHasDefinedSides = (match: any) =>
    Boolean(
      String(match?.dupla1Id || "").trim() &&
      String(match?.dupla2Id || "").trim() &&
      pairById.has(String(match?.dupla1Id || "").trim()) &&
      pairById.has(String(match?.dupla2Id || "").trim()),
    );

  const hasUncheckedParticipants = (
    sides: [MatchCheckInSide, MatchCheckInSide],
  ) =>
    sides.some(
      (side) =>
        side.participantCpfs.some(Boolean) &&
        !sideIsPresent(side, participantCheckins),
    );

  const courtTexts = siteTexts.challengeEngine.courtStatus;
  const onCourtMatches = useMemo(
    () =>
      rounds.flatMap((round: any[], roundIndex: number) =>
        (Array.isArray(round) ? round : [])
          .map((match, matchIndex) => ({
            match,
            matchIndex,
            roundIndex,
            labelA: resolveRoundPairLabelWithPoints(
              match?.dupla1Id,
              roundIndex,
            ),
            labelB: resolveRoundPairLabelWithPoints(
              match?.dupla2Id,
              roundIndex,
            ),
          }))
          .filter((item) => normalizeMatchStatus(item.match) === "in_progress"),
      ),
    [resolveRoundPairLabelWithPoints, rounds],
  );
  const registeredCourtsCount = Math.floor(Number(courtsCount || 0));
  const showCourtControls =
    !resultsOnly && !isPoco && !isTournamentChallenge;
  const shouldShowGlobalCheckIn =
    showCourtControls && globalCheckInSides.length > 0;

  const buildCheckInSides = (
    match: any,
    roundIndex: number,
    matchIndex: number,
  ): [MatchCheckInSide, MatchCheckInSide] => {
    const pairA = pairById.get(String(match?.dupla1Id || "").trim());
    const pairB = pairById.get(String(match?.dupla2Id || "").trim());
    return [
      {
        id: `challenge-${roundIndex}-${matchIndex}-A`,
        label: pairA
          ? `${pairA.nome1 || "Jogador"} / ${pairA.nome2 || "Jogador"}`
          : resolveRoundPairLabelWithPoints(match?.dupla1Id, roundIndex),
        participantCpfs: pairA ? [pairA.cpf1 || "", pairA.cpf2 || ""] : [],
      },
      {
        id: `challenge-${roundIndex}-${matchIndex}-B`,
        label: pairB
          ? `${pairB.nome1 || "Jogador"} / ${pairB.nome2 || "Jogador"}`
          : resolveRoundPairLabelWithPoints(match?.dupla2Id, roundIndex),
        participantCpfs: pairB ? [pairB.cpf1 || "", pairB.cpf2 || ""] : [],
      },
    ];
  };

  if (isTournamentChallenge) {
    return null;
  }

  return (
    <div className="field admin-rounds-editor">
      <span>{siteTexts.challengeEngine.labels.round}</span>
      {timerTotalSeconds > 0 && (
        <div className="admin-round-timer">
          <strong>{timerLabel}</strong>
          <div className="admin-round-timer-actions">
            <button
              type="button"
              className="pill-button"
              onClick={() => setTimerRunning(true)}
              disabled={timerRunning || remainingSeconds <= 0}
            >
              {customTexts.startTimer}
            </button>
            <button
              type="button"
              className="pill-button"
              onClick={() => setTimerRunning(false)}
              disabled={!timerRunning}
            >
              {customTexts.pauseTimer}
            </button>
            <button
              type="button"
              className="pill-button"
              onClick={() => {
                setTimerRunning(false);
                setRemainingSeconds(timerTotalSeconds);
              }}
            >
              {customTexts.resetTimer}
            </button>
          </div>
          {remainingSeconds <= 0 && (
            <p className="status-message">{customTexts.timeOver}</p>
          )}
        </div>
      )}
      {rounds.length === 0 ? (
        <EmptyState
          className="admin-empty-state"
          icon={<span className="material-symbols-outlined">event_busy</span>}
          title={emptyMessage}
        />
      ) : (
        <>
          {shouldShowGlobalCheckIn && (
            <section className="admin-global-checkin">
              <div className="admin-global-checkin-head">
                <strong>{siteTexts.challengeEngine.checkIn.confirm}</strong>
                <span>
                  {
                    globalCheckInSides.filter((side) =>
                      sideIsPresent(side, participantCheckins),
                    ).length
                  }
                  /{globalCheckInSides.length}
                </span>
              </div>
              <div className="admin-global-checkin-list">
                {globalCheckInSides.map((side) => {
                  const present = sideIsPresent(side, participantCheckins);
                  const pending = pendingCheckInSideId === side.id;
                  return (
                    <article
                      className="admin-global-checkin-item"
                      key={side.id}
                    >
                      <div>
                        <strong>{side.label}</strong>
                        {side.participants && side.participants.length > 0 && (
                          <small>
                            {normalizeCpf(side.participants[0]?.cpf)}
                          </small>
                        )}
                      </div>
                      <span className={present ? "is-present" : "is-waiting"}>
                        {present
                          ? siteTexts.challengeEngine.checkIn.present
                          : siteTexts.challengeEngine.checkIn.waitingArrival}
                      </span>
                      {onToggleCheckInSide && (
                        <button
                          type="button"
                          className="pill-button"
                          disabled={pending}
                          onClick={() => toggleGlobalCheckInSide(side, !present)}
                        >
                          {present
                            ? siteTexts.challengeEngine.checkIn.undo
                            : siteTexts.challengeEngine.checkIn.confirm}
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>
          )}
          {showCourtControls && onCourtMatches.length > 0 && (
            <section className="admin-on-court-summary">
              <div className="admin-on-court-summary-head">
                <strong>{courtTexts.onCourtGames}</strong>
                <span>{onCourtMatches.length}</span>
              </div>
              {registeredCourtsCount > 0 &&
                onCourtMatches.length > registeredCourtsCount && (
                  <p className="admin-on-court-warning">
                    {courtTexts.courtCapacityWarning} {registeredCourtsCount}{" "}
                    {courtTexts.registeredCourts}.
                  </p>
                )}
              <div className="admin-on-court-list">
                {onCourtMatches.map((item, index) => (
                  <div
                    className="admin-on-court-item"
                    key={`on-court-${item.match?.id || `${item.roundIndex}-${item.matchIndex}`}`}
                  >
                    <span>
                      {siteTexts.challengeEngine.labels.round}{" "}
                      {item.roundIndex + 1}
                      {registeredCourtsCount > 0
                        ? ` • ${courtTexts.court} ${Math.min(index + 1, registeredCourtsCount)}`
                        : ""}
                    </span>
                    <strong>{item.labelA}</strong>
                    <small>vs</small>
                    <strong>{item.labelB}</strong>
                  </div>
                ))}
              </div>
            </section>
          )}
          <div className="admin-rounds-list">
            {rounds.map((round: any[], roundIndex: number) => {
              const roundGroup = resolveSuper8RoundGroup(
                roundIndex,
                rounds.length,
                isSuper8,
                participantsCountForGroup,
                challengeDetail?.status || "pending",
              );
              const roundClassName = [
                "admin-round",
                roundGroup
                  ? `admin-round-group-${roundGroup.toLowerCase()}`
                  : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <div key={`round-${roundIndex}`} className={roundClassName}>
                  <h4 className="admin-round-title">
                    <span>
                      {isPoco && roundIndex === rounds.length - 1
                        ? finalLabel
                        : `${siteTexts.challengeEngine.labels.round} ${
                            roundIndex + 1
                          }`}
                    </span>
                    {roundGroup && (
                      <span
                        className={`admin-round-group-badge group-${roundGroup.toLowerCase()}`}
                      >
                        Grupo {roundGroup}
                      </span>
                    )}
                  </h4>

                  {round.map((match: any, matchIndex: number) => {
                    const isPendingMatch =
                      isPoco && isPendingPocoMatch(match, pairLabelById);
                    if (isPendingMatch) return (
                      <div key={`pending-${roundIndex}-${matchIndex}`} className="admin-round-match admin-round-match-pending">
                        <span className="admin-poco-court-heading">{`${courtTexts.court} ${matchIndex + 1}`}</span>
                        <span className="admin-round-pair">{siteTexts.challengeEngine.labels.removedPair}</span>
                      </div>
                    );
                    const checkInSides = buildCheckInSides(
                      match,
                      roundIndex,
                      matchIndex,
                    );
                    const matchStatus = normalizeMatchStatus(match);
                    const isOnCourt = matchStatus === "in_progress";
                    const isFinished = matchStatus === "finished";
                    const hasDefinedSides = matchHasDefinedSides(match);
                    const pendingCourtUpdate =
                      Number(match?.id || 0) > 0 &&
                      pendingCourtMatchId === Number(match.id);
                    const canChangeCourtStatus =
                      showCourtControls &&
                      Boolean(onToggleCourtStatus) &&
                      hasDefinedSides &&
                      !isFinished;
                    return (
                      <div
                        key={`match-${roundIndex}-${matchIndex}`}
                        className={[
                          isPoco
                            ? "admin-round-match admin-round-match-poco"
                            : "admin-round-match",
                          isOnCourt ? "is-on-court" : "",
                          isFinished ? "is-finished" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {isPoco ? (
                          <>
                          <div className="admin-poco-court-heading">{`${courtTexts.court} ${matchIndex + 1}`}</div>
                          <span className="admin-round-pair">{resolveRoundPairLabelWithPoints(match.dupla1Id, roundIndex)}</span>
                          <div className="admin-round-result-input">
                            <select
                              aria-label={`${siteTexts.challengeEngine.labels.round} ${roundIndex + 1}, ${courtTexts.court} ${matchIndex + 1}`}
                              value={
                                resolvedWinnerLoser(match)?.winner ===
                                match.dupla1Id
                                  ? "dupla1"
                                  : resolvedWinnerLoser(match)?.winner ===
                                      match.dupla2Id
                                    ? "dupla2"
                                    : ""
                              }
                              onChange={(event) =>
                                onUpdatePocoWinner(
                                  roundIndex,
                                  matchIndex,
                                  event.target.value as WinnerSide,
                                )
                              }
                            >
                              <option value="">Selecionar vencedora</option>
                              <option value="dupla1">
                                {resolveRoundPairLabelWithPoints(
                                  match.dupla1Id,
                                  roundIndex,
                                )}
                              </option>
                              <option value="dupla2">
                                {resolveRoundPairLabelWithPoints(
                                  match.dupla2Id,
                                  roundIndex,
                                )}
                              </option>
                            </select>
                            {!isSuper8 && (
                              <div className="admin-round-quick-actions">
                                {showClearAction && onClearWinner && (
                                  <button
                                    type="button"
                                    className="pill-button"
                                    onClick={() =>
                                      onClearWinner(roundIndex, matchIndex)
                                    }
                                  >
                                    Limpar
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="admin-round-pair">{resolveRoundPairLabelWithPoints(match.dupla2Id, roundIndex)}</span>
                          </>
                        ) : (
                          <div className="admin-round-result-input">
                            {showCourtControls && (
                              <div className="admin-round-court-status">
                                <span
                                  className={`admin-round-court-badge is-${matchStatus}`}
                                >
                                  {isOnCourt
                                    ? courtTexts.onCourt
                                    : isFinished
                                      ? courtTexts.finished
                                      : courtTexts.waiting}
                                </span>
                                {!hasDefinedSides && (
                                  <small>
                                    {courtTexts.undefinedParticipants}
                                  </small>
                                )}
                                {canChangeCourtStatus && (
                                  <button
                                    type="button"
                                    className="pill-button"
                                    disabled={pendingCourtUpdate}
                                    onClick={() => {
                                      if (
                                        !isOnCourt &&
                                        hasUncheckedParticipants(
                                          checkInSides,
                                        ) &&
                                        !window.confirm(
                                          courtTexts.confirmStartWithUnchecked,
                                        )
                                      ) {
                                        return;
                                      }
                                      onToggleCourtStatus?.(
                                        roundIndex,
                                        matchIndex,
                                        match,
                                        isOnCourt ? "pending" : "in_progress",
                                      );
                                    }}
                                  >
                                    {isOnCourt
                                      ? courtTexts.removeFromCourt
                                      : courtTexts.putOnCourt}
                                  </button>
                                )}
                              </div>
                            )}
                            {showCourtControls && (isOnCourt || isFinished) && (
                              <strong className="admin-round-score-action-label">
                                {courtTexts.addResult}
                              </strong>
                            )}
                            <div className="admin-round-score-row">
                              <label className="admin-round-score-side">
                                <span className="admin-round-score-pair-name">
                                  {resolveRoundPairLabelWithPoints(
                                    match.dupla1Id,
                                    roundIndex,
                                  )}
                                </span>
                                <input
                                  type="number"
                                  min={0}
                                  max={scoreMax}
                                  step={1}
                                  inputMode="numeric"
                                  className="admin-round-score-input"
                                  disabled={
                                    showCourtControls &&
                                    !isOnCourt &&
                                    !isFinished
                                  }
                                  title={
                                    showCourtControls &&
                                    !isOnCourt &&
                                    !isFinished
                                      ? courtTexts.resultRequiresCourt
                                      : undefined
                                  }
                                  value={match.score1}
                                  onChange={(event) =>
                                    onUpdateRoundScore(
                                      roundIndex,
                                      matchIndex,
                                      "score1",
                                      event.target.value,
                                    )
                                  }
                                />
                              </label>
                              <span className="admin-round-vs">X</span>
                              <label className="admin-round-score-side">
                                <span className="admin-round-score-pair-name">
                                  {resolveRoundPairLabelWithPoints(
                                    match.dupla2Id,
                                    roundIndex,
                                  )}
                                </span>
                                <input
                                  type="number"
                                  min={0}
                                  max={scoreMax}
                                  step={1}
                                  inputMode="numeric"
                                  className="admin-round-score-input"
                                  disabled={
                                    showCourtControls &&
                                    !isOnCourt &&
                                    !isFinished
                                  }
                                  title={
                                    showCourtControls &&
                                    !isOnCourt &&
                                    !isFinished
                                      ? courtTexts.resultRequiresCourt
                                      : undefined
                                  }
                                  value={match.score2}
                                  onChange={(event) =>
                                    onUpdateRoundScore(
                                      roundIndex,
                                      matchIndex,
                                      "score2",
                                      event.target.value,
                                    )
                                  }
                                />
                              </label>
                            </div>
                            {!isSuper8 && (
                              <div className="admin-round-quick-actions" />
                            )}
                          </div>
                        )}

                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
